import {
  type CustomToolParameter,
  CustomToolParameterType,
} from "@repo/app-types/entities/tools/custom";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import type { FC } from "react";

interface CustomToolParameterFormBuilderProps {
  label: string;
  description: string;
  value: readonly CustomToolParameter[];
  onChange: (parameters: CustomToolParameter[]) => void;
}

const parameterTypeOptions: SimpleSelectOption<CustomToolParameterType>[] = [
  { value: CustomToolParameterType.STRING, label: "String" },
  { value: CustomToolParameterType.NUMBER, label: "Number" },
  { value: CustomToolParameterType.BOOLEAN, label: "Boolean" },
  { value: CustomToolParameterType.OBJECT, label: "Object" },
  { value: CustomToolParameterType.ARRAY, label: "Array" },
];

const arrayItemTypeOptions: SimpleSelectOption<
  Exclude<CustomToolParameterType, "array">
>[] = [
  { value: CustomToolParameterType.STRING, label: "String" },
  { value: CustomToolParameterType.NUMBER, label: "Number" },
  { value: CustomToolParameterType.BOOLEAN, label: "Boolean" },
  { value: CustomToolParameterType.OBJECT, label: "Object" },
];

export const CustomToolParameterFormBuilder: FC<
  CustomToolParameterFormBuilderProps
> = ({ label, description, value, onChange }) => {
  const handleAddParameter = (): void => {
    onChange([
      ...value,
      {
        name: "",
        description: "",
        type: CustomToolParameterType.STRING,
        required: false,
      },
    ]);
  };

  const handleRemoveParameter = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleUpdateParameter = (
    index: number,
    updates: Partial<CustomToolParameter>,
  ): void => {
    const updated = [...value];
    const existingParam = updated[index];
    if (!existingParam) {
      return;
    }
    const nextParam = { ...existingParam, ...updates };

    // Clear array_item_type if type is no longer array
    if (updates.type && updates.type !== CustomToolParameterType.ARRAY) {
      delete nextParam.array_item_type;
    }
    updated[index] = nextParam;

    onChange(updated);
  };

  return (
    <div className="flex flex-col space-y-3">
      <Label size={Size.SMALL}>{label}</Label>
      <p className="text-sm text-muted-foreground">{description}</p>

      {value.map((param, index) => (
        <div
          key={index}
          className="flex flex-col gap-2 rounded-md border border-border-light bg-gray-50 p-3"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                label="Name"
                placeholder="Parameter name"
                value={param.name}
                onChange={(e) =>
                  handleUpdateParameter(index, { name: e.target.value })
                }
                maxLength={100}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Description"
                placeholder="Optional description"
                value={param.description ?? ""}
                onChange={(e) =>
                  handleUpdateParameter(index, { description: e.target.value })
                }
                maxLength={2048}
              />
            </div>
            <div className="pt-7">
              <IconButton
                icon={<IconTrash className="h-4 w-4" />}
                variant={ButtonVariant.Tertiary}
                onClick={() => handleRemoveParameter(index)}
                accessibleLabel={`Remove ${param.name || "parameter"}`}
              />
            </div>
          </div>

          <div className="flex items-end gap-4">
            <div className="w-32">
              <SimpleSelect
                label="Type"
                options={parameterTypeOptions}
                value={param.type}
                onChange={(type) => handleUpdateParameter(index, { type })}
              />
            </div>

            {param.type === CustomToolParameterType.ARRAY ? (
              <div className="w-32">
                <SimpleSelect
                  label="Item type"
                  options={arrayItemTypeOptions}
                  value={
                    param.array_item_type ?? CustomToolParameterType.STRING
                  }
                  onChange={(array_item_type) =>
                    handleUpdateParameter(index, { array_item_type })
                  }
                />
              </div>
            ) : null}

            <div className="pb-2">
              <Checkbox
                label="Required"
                isChecked={param.required ?? false}
                onChange={(required) =>
                  handleUpdateParameter(index, { required })
                }
              />
            </div>
          </div>
        </div>
      ))}

      <div>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.MEDIUM}
          icon={<IconPlus className="h-4 w-4" />}
          onClick={handleAddParameter}
        >
          Add parameter
        </Button>
      </div>
    </div>
  );
};
